import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { apiClient, useAuthStore, API_PRX } from '@indipia/shared'

/* ── 타입 ──────────────────────────────────────────────────────── */
export interface WorkTypeCode {
  workTypeCode:   string
  workTypeName:   string
  displayOrder:   number
  workTypeColor:  string | null
  autoScheduleYn: string | null
  /** Y=스케줄에만 표시되는 행정 공정(세대동의서청구 등). 시공파트너 배정 대상 아님 */
  scheduleOnlyYn?: string | null
}

export interface TaskStatusCode {
  statusCode: string
  statusName: string
}

export interface MilestoneTypeCode {
  typeCode: string
  typeName: string
}

export interface CommonCodes {
  workTypes:      WorkTypeCode[]
  scheduleStatuses: TaskStatusCode[]
  milestoneTypes: MilestoneTypeCode[]
}

export interface SiteStatusCode {
  code:     string
  codeName: string
}

// 현장/스케줄 진행상태(E01_SCHEDULE_STATUS) — sv-indipia 소관 공통코드라 schedule
// 서비스의 /schedule/common-codes 가 아니라 /indipia/codes 로 별도 조회한다(work-log
// 에이전트의 scheduleStatus.ts와 동일한 코드그룹·계약). 조회 전/실패 시엔 빈 배열 —
// 소비 측(getSiteStatusBadge)이 하드코딩 라벨로 대체한다.
const SITE_STATUS_GROUP = 'E01_SCHEDULE_STATUS'

/* ── Context ───────────────────────────────────────────────────── */
const CommonCodesContext = createContext<{
  codes:        CommonCodes | null
  loading:      boolean
  siteStatuses: SiteStatusCode[]
}>({ codes: null, loading: false, siteStatuses: [] })

/* ── Provider ──────────────────────────────────────────────────── */
export function CommonCodesProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore(s => s.user)

  const [codes,        setCodes]        = useState<CommonCodes | null>(null)
  const [loading,      setLoading]      = useState(false)
  const [siteStatuses, setSiteStatuses] = useState<SiteStatusCode[]>([])

  useEffect(() => {
    setLoading(true)
    const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    apiClient
      .get<{ data: CommonCodes }>('/schedule/common-codes', { headers })
      .then(res => setCodes(res.data.data ?? null))
      .catch(() => setCodes(null))
      .finally(() => setLoading(false))

    apiClient
      .get<{ data: Array<{ codeValue: string; codeName: string }> }>(`${API_PRX.INDIPIA}/codes`, {
        headers, params: { codeGroupCode: SITE_STATUS_GROUP },
      })
      .then(res => setSiteStatuses((res.data.data ?? []).map(it => ({ code: it.codeValue, codeName: it.codeName }))))
      .catch(() => setSiteStatuses([]))
  }, [user?.compId])

  return (
    <CommonCodesContext.Provider value={{ codes, loading, siteStatuses }}>
      {children}
    </CommonCodesContext.Provider>
  )
}

/* ── Hook ──────────────────────────────────────────────────────── */
export function useCommonCodes() {
  return useContext(CommonCodesContext)
}
